"use client";

import { useHubspotForm } from "hooks/useHubspotForm";
import { useRef, useState } from "react";
import { FormHandles } from "@unform/core";
import { useRouter } from "next/router";
import { useLocale, YuLifeLocale } from "@hooks";
import { ukPhoneNumberRegex, usPhoneNumberRegex, zaPhoneNumberRegex } from "utils/regex";
import { HubspotFormProps } from "./HubspotAutoForm";
import * as Yup from "yup";
import { getCookie, isAllowedEmail, parseQuery } from "@utils";
import { PERSONAL_DOMAIN_EMAILS } from "utils/_constants";
import { getHubspotFieldsFromSearchQuery, getValidationErrors } from "utils/form";
import { StaticRoutes } from "@routes";

const useHubspotFormState = ({ formId, fields, redirectUrl }: HubspotFormProps) => {
  const [error, setError] = useState("");
  const [isShaking, setIsShaking] = useState(false);
  const { handleFormSubmit, loading } = useHubspotForm();
  const formRef = useRef<FormHandles>(null);
  const router = useRouter();
  const { locale } = useLocale();

  // Submit to Hubspot callback
  const submitFormToHubspot = async (formState: object) => {
    const formattedFields = Object.entries(formState).map(([name, value]) => {
      const fieldMeta = fields.find((f) => f.name === name);

      if (fieldMeta?.type === "multiselect" && typeof value === "string") {
        return { name, value: value.split(",").join(";") };
      }

      return { name, value };
    });

    const hutk = getCookie("hubspotutk");

    if (window.location.search) {
      formattedFields.push(...getHubspotFieldsFromSearchQuery(parseQuery(window.location.search)));
    }

    const href = window?.location?.href;

    formattedFields.push({
      name: "country",
      value: locale || "uk",
    });

    const formData = {
      fields: formattedFields,
      context: {
        hutk,
        pageUri: href,
        pageName: typeof document !== "undefined" ? document.title : "",
      },
    };
    try {
      const results = await handleFormSubmit(formId, formData);

      const hasErrors =
        ("status" in results && results.status === "error") ||
        ("errors" in results && results.errors?.length > 0);

      if (hasErrors) {
        return false;
      }

      // redirect here
      router.push(redirectUrl || StaticRoutes.formSuccess);

      return true;
    } catch (e) {
      // Bugsnag.notify(e);
      return false;
    }
  };

  const submit = async (formState) => {
    try {
      const schema = Yup.object().shape(buildSchema(fields, locale));

      await schema.validate(formState, {
        abortEarly: false,
      });

      await submitFormToHubspot(formState);
    } catch (err) {
      setIsShaking(true);

      setTimeout(() => {
        setIsShaking(false);
      }, 820);

      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);

        if (formRef.current) {
          formRef.current?.setErrors(errors);
        }
      }

      setError("Check the fields");
    }
  };

  return {
    submit,
    error,
    isShaking,
    formRef,
    loading,
  };
};

export default useHubspotFormState;

const buildSchema = (fields: HubspotFormProps["fields"], locale: YuLifeLocale) => {
  const schema = {} as Yup.ObjectShape;

  for (const field of fields) {
    const f = Yup.string();

    if (field.isRequired) {
      f.required(`${field.label} is required`);
    }

    if (field.type === "email") {
      f.email().test("is-personal-email", (value) => isAllowedEmail(value, PERSONAL_DOMAIN_EMAILS));
    }

    if (field.type === "phone") {
      f.test("is-valid-phone-number", (value) => validatePhoneNumber(value, locale));
    }
    schema[field.name] = f;
  }

  return schema;
};

const validatePhoneNumber = (phoneNumber: string, locale: YuLifeLocale) => {
  if (
    (locale === "uk" && !ukPhoneNumberRegex.test(phoneNumber)) ||
    (locale === "us" && !usPhoneNumberRegex.test(phoneNumber)) ||
    (locale === "za" && !zaPhoneNumberRegex.test(phoneNumber))
  ) {
    return false;
  }
  return true;
};
